// Adding a passkey. The one-time code comes after "#", so it never reaches server logs.

import { startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import type { MeDTO } from "../../shared/dto.js";
import { api, ApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export function EnrollPage({ me }: { me: MeDTO }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code] = useState(() => {
    const value = window.location.hash.slice(1);
    // Keep the code out of the address bar and history.
    if (value) window.history.replaceState(null, "", window.location.pathname);
    return value || undefined;
  });
  const [label, setLabel] = useState(() => guessDeviceLabel());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const owner = me.role === "owner" && !me.preview;
  if (!code && !owner) {
    return (
      <Shell>
        <p className="text-sm text-muted">This page needs the one-time link printed by the enroll command.</p>
      </Shell>
    );
  }

  async function enroll() {
    setBusy(true);
    setError(null);
    try {
      const options = await api.post<PublicKeyCredentialCreationOptionsJSON>("/api/auth/register/options", { code });
      const response = await startRegistration({ optionsJSON: options });
      await api.post("/api/auth/register/verify", { code, label, response });
      await queryClient.invalidateQueries();
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error && e.name === "NotAllowedError" ? "Cancelled." : "Couldn't create the passkey.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <label className="mb-4 block text-sm">
        <span className="mb-1 block text-muted">Name for this passkey</span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={60}
          className="h-10 w-full rounded-lg border border-line bg-surface px-3 outline-none focus:border-primary"
        />
      </label>
      <Button variant="primary" size="lg" className="w-full" onClick={enroll} disabled={busy}>
        <KeyRound /> Create a passkey
      </Button>
      {error && <p className="mt-3 text-sm text-danger">{error}</p>}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-sm p-8">
        <h1 className="mb-1 text-lg font-semibold">Add a passkey</h1>
        <p className="mb-6 text-sm text-muted">You'll use it to sign in to NS Journal.</p>
        {children}
      </Card>
    </main>
  );
}

function guessDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iPhone or iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "Passkey";
}
